import Vue from "vue";
import VueI18n, { LocaleMessages, Values } from "vue-i18n";
import hu from "@/locales/hu.json";
import en from "@/locales/en.json";
import de from "@/locales/de.json";

Vue.use(VueI18n);

/** A kiválasztott felületi nyelv kulcsa a böngésző localStorage-ában. */
export const LANGUAGE_STORAGE_KEY = "trufinance.language";

export const DEFAULT_LANGUAGE = "hu";

export const SUPPORTED_LANGUAGES = ["hu", "en", "de"] as const;

export type Language = typeof SUPPORTED_LANGUAGES[number];

/** A nyelvhez tartozó kultúrakód (Kendo üzenetekhez). A szám- és dátumformátum ettől függetlenül magyar marad. */
export const LANGUAGE_CULTURES: Record<Language, string> = {
  hu: "hu-HU",
  en: "en-US",
  de: "de-DE",
};

/** A nyelvválasztóban megjelenő (nem fordított, saját nyelvű) megnevezések. */
export const LANGUAGE_NAMES: Record<Language, string> = {
  hu: "Magyar",
  en: "English",
  de: "Deutsch",
};

export function normalizeLanguage(code: string | null | undefined): Language | null {
  if (!code) return null;
  const lower = code.trim().toLowerCase();
  const found = SUPPORTED_LANGUAGES.find((l) => l === lower || lower.startsWith(l + "-"));
  return found || null;
}

/** A tárolt nyelv, hiányzó vagy ismeretlen érték esetén magyar. */
export function getLanguage(): Language {
  try {
    return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY)) || DEFAULT_LANGUAGE;
  } catch (e) {
    return DEFAULT_LANGUAGE;
  }
}

const i18n = new VueI18n({
  locale: getLanguage(),
  fallbackLocale: DEFAULT_LANGUAGE,
  messages: ({ hu, en, de } as unknown) as LocaleMessages,
  silentFallbackWarn: true,
  silentTranslationWarn: true,
});

/** Nyelv eltárolása; az alkalmazás újratöltése után lép életbe. */
export function setLanguage(language: Language) {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (e) {
    // localStorage nem elérhető: a nyelv csak az aktuális oldalbetöltésre érvényes
  }
  i18n.locale = language;
}

/**
 * Fordítás komponensen kívüli kódból (service, model, router, script blokk).
 * Sablonban a $t használandó. Helyőrzők: {0}, {1} ... (lista) vagy {nev} (objektum).
 */
export function t(key: string, values?: Values): string {
  return i18n.t(key, values) as string;
}

export default i18n;
